import React, { useEffect, useLayoutEffect, useRef, memo } from 'react';
import { useTheme } from '../../assets/contexts/ThemeContext';
import { usePageLayout } from '../../assets/contexts/PageLayoutContext';
import SubSidebarMenu from './SubSidebarMenu';
import SubSidebarMenuTree from './SubSidebarMenuTree';
import SubSidebarMenuTab from './SubSidebarMenuTab';
import SidebarSkeleton from '../../components/skeleton/SidebarSkeleton';

/**
 * 사이드바 컴포넌트 (독립 렌더링용)
 * 서브메뉴(아코디언/트리/탭) 하단에 필터 영역(wrap)을 두고, Context로 받은 필터 컴포넌트를 옵션으로 렌더.
 * 페이지 세로 스크롤 시 서브메뉴와 필터가 함께 스크롤됨.
 *
 * @param {Object} sidebarMenu - 사이드바 메뉴 데이터 { title, menu[], activeHref, enabled, position? }
 * @param {boolean} sidebarLoading - 사이드바 로딩 상태
 */
const Sidebar = memo(({ sidebarMenu, sidebarLoading = false }) => {
  const themeContext = useTheme();
  const { leftSidebarFilterContent, leftSidebarOptions } = usePageLayout();
  const showSubmenu = leftSidebarOptions?.showSubmenu !== false; // 기본 true
  const themeConfig = themeContext?.themeConfig || {};
  const sidebarMenuType = themeConfig.sidebarMenuType || 'accordion';
  const sidebarColumns = themeConfig.sidebarColumns || 3;
  const sidebarContainerRef = useRef(null);

  // window.SidebarMenuData 설정 (원본과 동일)
  useEffect(() => {
    if (sidebarMenu) {
      window.SidebarMenuData = sidebarMenu;
    }
  }, [sidebarMenu]);

  // 사이드바 메뉴 타입 적용 (원본 sidebar-script와 동일)
  useEffect(() => {
    if (!sidebarContainerRef.current) return;

    const applySidebarMenuType = (type) => {
      const sidebarContainer = sidebarContainerRef.current;
      if (!sidebarContainer) return;

      const allWrappers = sidebarContainer.querySelectorAll('.sidebar-menu-wrapper');
      allWrappers.forEach((wrapper) => {
        wrapper.style.display = 'none';
      });

      const selectedWrapper = sidebarContainer.querySelector(`[data-sidebar-menu-type="${type}"]`);
      if (selectedWrapper) {
        selectedWrapper.style.display = '';
      }
    };

    applySidebarMenuType(sidebarMenuType);

    // 전역 함수로 노출 (원본과 동일)
    window.applySidebarMenuType = applySidebarMenuType;
  }, [sidebarMenuType]);

  const position = sidebarMenu?.position || 'left';
  const sidebarId = `sidebar-${position}-container`;
  const sidebarColClass = `col-12 col-lg-${sidebarColumns}`;

  // 사이드바 컬럼 수 변경 시 DOM 업데이트 (테마 원격 제어기와 연동)
  useLayoutEffect(() => {
    if (!sidebarMenu || sidebarMenu.enabled === false) return;

    const numValue = parseInt(sidebarColumns, 10);
    if (isNaN(numValue) || numValue < 1 || numValue > 11) return;

    const contentColumns = 12 - numValue;

    // DOM 업데이트 함수
    const updateSidebarColumns = () => {
      const sidebarElement = sidebarContainerRef.current;
      if (!sidebarElement) return;

      // 사이드바 요소의 클래스 업데이트
      const sidebarClassList = sidebarElement.classList;
      for (let i = sidebarClassList.length - 1; i >= 0; i--) {
        const className = sidebarClassList[i];
        if (className.match(/^col-(xs|sm|md|lg|xl|xxl)-\d+$/)) {
          sidebarClassList.remove(className);
        }
      }
      sidebarElement.classList.add('col-lg-' + numValue);

      // 사이드바 옆의 콘텐츠 영역 찾기
      let contentElement = null;
      if (position === 'left') {
        contentElement = sidebarElement.nextElementSibling;
      } else {
        contentElement = sidebarElement.previousElementSibling;
      }

      if (contentElement) {
        const contentClassList = contentElement.classList;
        // col-lg-* 클래스 제거
        for (let i = contentClassList.length - 1; i >= 0; i--) {
          const className = contentClassList[i];
          if (className.match(/^col-(xs|sm|md|lg|xl|xxl)-\d+$/)) {
            contentClassList.remove(className);
          }
        }
        // 새로운 컬럼 클래스 추가
        contentElement.classList.add('col-lg-' + contentColumns);
      }

      // CSS 변수 업데이트
      document.documentElement.style.setProperty('--sidebar-columns', numValue.toString());
    };

    // 즉시 실행
    updateSidebarColumns();

    // MutationObserver를 사용하여 DOM 변경 감지 및 복원
    const observer = new MutationObserver((mutations) => {
      let needsUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target === sidebarContainerRef.current ||
              (target.classList.contains('col-12') &&
               (target === sidebarContainerRef.current?.nextElementSibling ||
                target === sidebarContainerRef.current?.previousElementSibling))) {
            // 현재 클래스 확인
            const currentSidebarClass = sidebarContainerRef.current?.className.match(/col-lg-(\d+)/);
            const expectedSidebarClass = `col-lg-${numValue}`;
            if (currentSidebarClass && currentSidebarClass[0] !== expectedSidebarClass) {
              needsUpdate = true;
            }
          }
        }
      });
      if (needsUpdate) {
        updateSidebarColumns();
      }
    });

    // 사이드바와 콘텐츠 요소 관찰
    const sidebarElement = sidebarContainerRef.current;
    if (sidebarElement) {
      observer.observe(sidebarElement, { attributes: true, attributeFilter: ['class'] });
      const contentElement = position === 'left'
        ? sidebarElement.nextElementSibling
        : sidebarElement.previousElementSibling;
      if (contentElement) {
        observer.observe(contentElement, { attributes: true, attributeFilter: ['class'] });
      }
    }

    return () => {
      observer.disconnect();
    };
  }, [sidebarColumns, position, sidebarMenu]);

  if (!sidebarMenu || sidebarMenu.enabled === false || sidebarMenuType === 'none') {
    return null;
  }

  // 사이드바 로딩 중일 때 스켈레톤 UI 표시. leftSidebarOptions.showSubmenu === false 이면 서브메뉴 숨김(필터만 표시)
  const renderSidebar = () => {
    if (!showSubmenu) return null;
    if (sidebarLoading) {
      return <SidebarSkeleton position={position} />;
    }

    return (
      <>
        <div data-sidebar-menu-type="accordion" className="sidebar-menu-wrapper" style={{ display: sidebarMenuType === 'accordion' ? '' : 'none' }}>
          <SubSidebarMenu data={sidebarMenu} position={position} />
        </div>
        <div data-sidebar-menu-type="tree" className="sidebar-menu-wrapper" style={{ display: sidebarMenuType === 'tree' ? '' : 'none' }}>
          <SubSidebarMenuTree data={sidebarMenu} position={position} />
        </div>
        <div data-sidebar-menu-type="tab" className="sidebar-menu-wrapper" style={{ display: sidebarMenuType === 'tab' ? '' : 'none' }}>
          <SubSidebarMenuTab data={sidebarMenu} position={position} />
        </div>
      </>
    );
  };

  return (
    <div
      ref={sidebarContainerRef}
      className={`${sidebarColClass} sidebar-${position} sidebar-${position === 'left' ? 'left' : 'right'}`}
      id={sidebarId}
    >
      {renderSidebar()}
      {/* 필터 영역(wrap): Context에서 옵션으로 받은 필터 컴포넌트 렌더, 서브메뉴와 함께 스크롤, AOS 동일 적용 */}
      {leftSidebarFilterContent != null && (
        <div
          className={`sidebar-filter-wrap${showSubmenu ? ' mt-3' : ''}`}
          data-sidebar-filter-area
          data-aos={position === 'right' ? 'fade-left' : 'fade-right'}
          data-aos-delay="100"
        >
          {leftSidebarFilterContent}
        </div>
      )}
    </div>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
